import {
	DEFAULT_LINE_TYPES,
	IkeFieldType,
	isDefaultLineTitle,
	type LineSymbol,
	resolveLineSymbol,
} from '@ikegps/core-api';
import { FormModelField, type FormModelFieldState, FormModelMutatorField } from './form.field';

export interface FormModelFieldLineOption {
	/** Shape id — the base stroke/marker/label rendering (NOT a per-row identity). */
	symbolId: string;
	/** Display title — editable; the stable identity a collected line stores. Unique + non-empty within a field. */
	title: string;
	/** Color for this line type (HTML hex); the shape carries no color. */
	symbolColor: string;
	visible: boolean;
}

export interface FormModelFieldLineState extends FormModelFieldState {
	lineTypes: FormModelFieldLineOption[];
}

export class FormModelMutatorLine extends FormModelMutatorField<FormModelFieldLine> {
	private getLineTypes(): FormModelFieldLineOption[] {
		const lineTypes = this.field.stateValue.lineTypes;
		if (Array.isArray(lineTypes)) {
			return [...lineTypes];
		}
		return FormModelFieldLine.defaultOptions();
	}

	private write(lineTypes: FormModelFieldLineOption[]) {
		this.state.setFieldValue(this.field.fieldId, { ...this.field.stateValue, lineTypes });
	}

	/** Add a new line type, or replace the one at `index`. */
	option(option: FormModelFieldLineOption, index = -1) {
		const lineTypes = this.getLineTypes();
		if (index === -1 || index > lineTypes.length - 1) {
			lineTypes.push(option);
		} else {
			lineTypes[index] = option;
		}
		this.write(lineTypes);
	}

	options(lineTypes: FormModelFieldLineOption[]) {
		this.write(lineTypes);
	}

	remove(index: number) {
		const lineTypes = this.getLineTypes();
		if (index < 0 || index >= lineTypes.length) {
			throw new Error('Index is out of bounds ' + index);
		}
		lineTypes.splice(index, 1);
		this.write(lineTypes);
	}

	move(index: number, delta: number) {
		const lineTypes = this.getLineTypes();
		const target = index + delta;
		if (index < 0 || index >= lineTypes.length || target < 0 || target >= lineTypes.length) {
			return;
		}
		const [moved] = lineTypes.splice(index, 1);
		lineTypes.splice(target, 0, moved);
		this.write(lineTypes);
	}

	setTitle(index: number, title: string) {
		this.update(index, (option) => ({ ...option, title }));
	}

	setSymbol(index: number, symbolId: string, symbolColor: string) {
		this.update(index, (option) => ({ ...option, symbolId, symbolColor }));
	}

	setVisibility(index: number, visible: boolean) {
		this.update(index, (option) => ({ ...option, visible }));
	}

	toggleVisibility(index: number) {
		this.update(index, (option) => ({ ...option, visible: !option.visible }));
	}

	private update(index: number, change: (option: FormModelFieldLineOption) => FormModelFieldLineOption) {
		const lineTypes = this.getLineTypes();
		if (lineTypes[index] == null) {
			return;
		}
		lineTypes[index] = change(lineTypes[index]);
		this.write(lineTypes);
	}
}

export class FormModelFieldLine extends FormModelField<FormModelFieldLineState> {
	static isLineField(field: FormModelField): field is FormModelFieldLine {
		return field.type === IkeFieldType.LINE;
	}

	/**
	 * The default line-type library seeded on every new line field (all visible). Presets are non-editable
	 * (visibility-toggle-only); a row is a preset iff its title is in this set — no stored flag (see `isDefaultLineTitle`).
	 */
	static defaultOptions(): FormModelFieldLineOption[] {
		return DEFAULT_LINE_TYPES.map((type) => ({
			symbolId: type.symbolId,
			symbolColor: type.symbolColor,
			title: type.title,
			visible: true,
		}));
	}

	private _LineMutator: FormModelMutatorLine | null = null;
	override get Mutator(): FormModelMutatorLine {
		if (this._LineMutator == null) {
			this._LineMutator = new FormModelMutatorLine(this);
		}
		return this._LineMutator;
	}

	get lineTypes(): FormModelFieldLineOption[] {
		return this.stateValue.lineTypes || FormModelFieldLine.defaultOptions();
	}

	get visibleLineTypes(): FormModelFieldLineOption[] {
		return this.lineTypes.filter((lineType) => lineType.visible);
	}

	private _lineTypeIndex: Map<string, FormModelFieldLineOption> | null = null;
	private _lineTypeIndexSource: FormModelFieldLineOption[] | null = null;

	/**
	 * Title→option lookup cached against the lineTypes array identity. Mutations always replace the array
	 * (mutator clones + writes a new one), so an identity miss rebuilds the map.
	 */
	private lineTypeIndex(): Map<string, FormModelFieldLineOption> {
		const lineTypes = this.lineTypes;
		if (this._lineTypeIndex == null || this._lineTypeIndexSource !== lineTypes) {
			const index = new Map<string, FormModelFieldLineOption>();
			for (const lineType of lineTypes) {
				if (!index.has(lineType.title)) {
					index.set(lineType.title, lineType);
				}
			}
			this._lineTypeIndex = index;
			this._lineTypeIndexSource = lineTypes;
		}
		return this._lineTypeIndex;
	}

	findLineType(title: string | null): FormModelFieldLineOption | null {
		if (title == null || title === '') {
			return null;
		}
		return this.lineTypeIndex().get(title) ?? null;
	}

	symbolForTitle(title: string | null): LineSymbol | null {
		const option = this.findLineType(title);
		if (option == null) {
			return null;
		}
		return resolveLineSymbol(option);
	}

	/**
	 * Row is a fixed preset only if it's the FIRST occurrence of a default-library title. A later row
	 * duplicating a preset title is a custom row kept editable/removable, NOT a preset.
	 */
	isPresetOption(index: number): boolean {
		const option = this.lineTypes[index];
		if (option == null || !isDefaultLineTitle(option.title)) {
			return false;
		}
		return this.lineTypes.findIndex((other) => other.title === option.title) === index;
	}

	get isValid(): boolean {
		const titles = this.lineTypes.map((lineType) => lineType.title.trim());
		const hasVisibleTitled = this.lineTypes.some((lineType) => lineType.visible && lineType.title.trim().length > 0);
		const hasEmpty = titles.some((title) => title.length === 0);
		const hasDuplicate = new Set(titles).size !== titles.length;
		return hasVisibleTitled && !hasEmpty && !hasDuplicate;
	}
}
